import React, { useEffect, useState, useMemo } from 'react';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import { supabase } from '../../lib/supabase';
import { generateInvoicePDF, generatePaymentPDF } from '../../utils/pdfGenerator';
import { Search, Download, Eye, FileText, AlertCircle } from 'lucide-react';
import { isLightClubBackground, normalizeHexColor } from '../../utils/clubProductexContrast';

type Doc = {
  id: string;
  tipo: string | null;
  numero: string | null;
  fecha: string | null;
  monto: number | null;
  url_archivo: string | null;
  descripcion: string | null;
  vencimiento?: string | null;
};

const ClienteDocumentos: React.FC = () => {
  const { cliente } = useClienteAuth();
  const [items, setItems] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'fecha' | 'monto' | 'tipo'>('fecha');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [balanceARS, setBalanceARS] = useState<number>(0);
  const [balanceUSD, setBalanceUSD] = useState<number>(0);
  const [clubPoints, setClubPoints] = useState<{
    current_level: string;
    total_points: number;
    points_balance: number;
    level_info: { min_points: number; max_points: number | null; color_primary: string };
    next_level?: { name: string; points_needed: number };
  } | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!cliente) return;
      setLoading(true);
      setError(null);
      try {
        // Cargar documentos del cliente (facturas y recibos de pago)
        const { data, error } = await supabase.rpc('portal_list_documentos', {
          p_portal_cliente_id: cliente.id
        });
        if (error) throw error;
        const documentos = Array.isArray(data) ? data : [];
        setItems(documentos);

        // Saldo idéntico al que ve el administrador en Clientes
        const { data: balanceData, error: balanceError } = await supabase.rpc('get_cliente_balance_for_portal', {
          p_portal_cliente_id: cliente.id
        });
        if (!balanceError && balanceData && balanceData.length > 0) {
          const row = balanceData[0];
          setBalanceARS(Number(row.saldo_ars ?? 0));
          setBalanceUSD(Number(row.saldo_usd ?? 0));
        }

        // Club Productex: nivel y puntos dinámicos (misma lógica que /cliente/club)
        const { data: clientData } = await supabase
          .from('clients')
          .select('id')
          .eq('portal_cliente_id', cliente.id)
          .maybeSingle();
        if (clientData?.id) {
          const { data: pointsData } = await supabase.rpc('get_client_points_breakdown', {
            p_client_id: clientData.id
          });
          if (pointsData) {
            const raw = Array.isArray(pointsData) ? pointsData[0] : pointsData;
            const d = typeof raw === 'object' && raw !== null ? raw : (typeof raw === 'string' ? JSON.parse(raw) : null);
            if (d) {
              const levelInfo = d.current_level || {};
              setClubPoints({
                current_level: levelInfo.name || 'Starter',
                total_points: Number(d.total_points ?? 0),
                points_balance: Number(d.points_balance ?? 0),
                level_info: {
                  min_points: levelInfo.min_points ?? 0,
                  max_points: levelInfo.max_points ?? null,
                  color_primary: levelInfo.color_primary ?? '#10B981'
                },
                next_level: d.next_level ? { name: d.next_level.name, points_needed: d.next_level.points_needed ?? 0 } : undefined
              });
            }
          }
        }

        // Log activity
        try {
          await supabase.rpc('log_cliente_activity', {
            p_cliente_id: cliente.id,
            p_action: 'Visualización de documentos',
            p_entity_type: 'documents',
            p_details: `Cliente visualizó ${documentos.length} documentos`,
            p_action_type: 'view'
          });
        } catch (logError) {
          console.warn('Error logging activity:', logError);
        }
      } catch (e: any) {
        console.error('Error cargando documentos:', e);
        setError('No se pudieron cargar los documentos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cliente]);

  // Filtrar y ordenar documentos
  const clubCardColors = useMemo(() => {
    const bg = normalizeHexColor(clubPoints?.level_info?.color_primary);
    const light = isLightClubBackground(bg);
    return {
      bg,
      light,
      border: light ? `${bg}99` : `${bg}cc`,
    };
  }, [clubPoints?.level_info?.color_primary]);

  const filteredAndSortedItems = useMemo(() => {
    let filtered = items;

    // Aplicar búsqueda
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.tipo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'fecha':
          aValue = a.fecha ? new Date(a.fecha).getTime() : 0;
          bValue = b.fecha ? new Date(b.fecha).getTime() : 0;
          break;
        case 'monto':
          aValue = a.monto || 0;
          bValue = b.monto || 0;
          break;
        case 'tipo':
          aValue = a.tipo || '';
          bValue = b.tipo || '';
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [items, searchTerm, sortBy, sortOrder]);

  const generateAndDownloadPDF = async (doc: Doc) => {
    try {
      const isInvoice = doc.tipo && ['A', 'B', 'C', 'X'].includes(doc.tipo.toUpperCase());
      if (isInvoice) {
        // Obtener datos completos de la factura
        const { data: invoiceData, error: invoiceError } = await supabase
          .from('invoices')
          .select(`
            *,
            items:invoice_items(*)
          `)
          .eq('number', doc.numero)
          .single();
        
        if (invoiceError) throw invoiceError;
        
        // Obtener datos del cliente
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('*')
          .eq('portal_cliente_id', cliente?.id)
          .single();
        
        if (clientError) throw clientError;
        
        // Generar y descargar PDF de factura
        await generateInvoicePDF(invoiceData, clientData.business_name || 'Cliente');

        // Log activity
        try {
          await supabase.rpc('log_cliente_activity', {
            p_cliente_id: cliente.id,
            p_action: 'Descarga de factura',
            p_entity_type: 'invoice',
            p_entity_id: invoiceData.id,
            p_details: `Cliente descargó factura ${doc.numero}`,
            p_action_type: 'view'
          });
        } catch (logError) {
          console.warn('Error logging activity:', logError);
        }
        
      } else if (doc.tipo === 'Recibo de Pago') {
        // Obtener datos completos del pago
        const { data: paymentData, error: paymentError } = await supabase
          .from('payments')
          .select(`
            *,
            invoice:invoices(*)
          `)
          .eq('id', doc.id)
          .single();
        
        if (paymentError) throw paymentError;
        
        // Obtener datos del cliente
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('*')
          .eq('portal_cliente_id', cliente?.id)
          .single();
        
        if (clientError) throw clientError;
        
        // Generar y descargar PDF de recibo
        await generatePaymentPDF(paymentData, clientData.business_name || 'Cliente');

        // Log activity
        try {
          await supabase.rpc('log_cliente_activity', {
            p_cliente_id: cliente.id,
            p_action: 'Descarga de recibo',
            p_entity_type: 'payment',
            p_entity_id: paymentData.id,
            p_details: `Cliente descargó recibo de pago ${doc.numero}`,
            p_action_type: 'view'
          });
        } catch (logError) {
          console.warn('Error logging activity:', logError);
        }
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF. Intenta más tarde.');
    }
  };

  const downloadFile = async (url: string, filename: string, doc?: Doc) => {
    try {
      if (url.includes('supabase.co')) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }

      // Log activity if document info is provided
      if (doc && cliente) {
        try {
          await supabase.rpc('log_cliente_activity', {
            p_cliente_id: cliente.id,
            p_action: 'Descarga de archivo',
            p_entity_type: 'document',
            p_entity_id: doc.id,
            p_details: `Cliente descargó archivo: ${filename}`,
            p_action_type: 'view'
          });
        } catch (logError) {
          console.warn('Error logging activity:', logError);
        }
      }
    } catch (error) {
      console.error('Error descargando archivo:', error);
      alert('Error al descargar el archivo. Intenta abrir el enlace en una nueva pestaña.');
      try {
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (fallbackError) {
        console.error('Error en fallback:', fallbackError);
      }
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number | null, currency: 'ARS' | 'USD' = 'ARS') => {
    if (amount === null) return currency === 'USD' ? 'USD 0' : '$0';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getTipoDisplay = (tipo: string | null) => {
    if (!tipo) return 'Sin especificar';
    
    switch (tipo.toUpperCase()) {
      case 'X':
        return 'Factura';
      case 'A':
        return 'Factura A';
      case 'B':
        return 'Factura B';
      case 'C':
        return 'Factura C';
      case 'RECIBO':
        return 'Recibo';
      case 'NOTA CREDITO':
      case 'NC':
        return 'Nota de Crédito';
      case 'NOTA DEBITO':
      case 'ND':
        return 'Nota de Débito';
      default:
        return tipo;
    }
  };

  const getTipoColor = (tipo: string | null) => {
    if (!tipo) return 'bg-gray-100 text-gray-800 border-gray-200';
    
    if (tipo.includes('Factura')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (tipo === 'Recibo de Pago') {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (tipo.includes('Nota de Crédito') || tipo.includes('NC')) {
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    } else if (tipo.includes('Nota de Débito') || tipo.includes('ND')) {
      return 'bg-red-100 text-red-800 border-red-200';
    } else {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando documentos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error al cargar documentos</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Mi Cuenta</h1>
        <p className="text-gray-600">Facturas, recibos y notas de tu cuenta</p>
      </div>

      {/* Club Productex® y Saldo del Cliente */}
      <div className="mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Club Productex® - Nivel y puntos dinámicos desde get_client_points_breakdown */}
          <div
            className={`lg:col-span-2 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
              clubCardColors.light ? 'text-gray-900' : 'text-white'
            }`}
            style={{
              backgroundColor: clubCardColors.bg,
              borderColor: clubCardColors.border,
            }}
          >
            <div className="text-center mb-3">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg ${
                  clubCardColors.light ? 'bg-black/10' : 'bg-white/25'
                }`}
              >
                <span className="text-2xl font-bold">🏆</span>
              </div>
              <h2
                className={`text-xl font-bold mb-2 drop-shadow-sm ${
                  clubCardColors.light ? 'text-gray-900' : 'text-white'
                }`}
              >
                Club Productex®
              </h2>
              <div className="mb-2">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    clubCardColors.light ? 'bg-black/10 text-gray-900' : 'bg-white/30 text-white'
                  }`}
                >
                  {clubPoints?.current_level || 'Starter'}
                </span>
              </div>
              <div
                className={`text-3xl font-black mb-2 tracking-tight ${
                  clubCardColors.light ? 'text-gray-900' : 'text-white'
                }`}
              >
                {(clubPoints?.total_points ?? 0).toLocaleString()} pts
              </div>
              <div className={`text-xs mb-3 ${clubCardColors.light ? 'text-gray-700' : 'text-white/90'}`}>
                Puntos acumulados (últimos 12 meses)
              </div>
            </div>
            {clubPoints?.next_level && clubPoints.next_level.points_needed > 0 ? (
              <div className="mb-3">
                <div className={`flex justify-between text-xs mb-1 ${clubCardColors.light ? 'text-gray-700' : 'text-white/90'}`}>
                  <span>Nivel Actual</span>
                  <span>Próximo: {clubPoints.next_level.name}</span>
                </div>
                <div
                  className={`w-full rounded-full h-1.5 shadow-inner ${
                    clubCardColors.light ? 'bg-black/10' : 'bg-white/25'
                  }`}
                >
                  <div
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      clubCardColors.light ? 'bg-gray-900' : 'bg-white'
                    }`}
                    style={{
                      width: `${Math.min(
                        100,
                        Math.max(
                          0,
                          ((clubPoints.total_points - clubPoints.level_info.min_points) /
                            (clubPoints.next_level.points_needed + (clubPoints.total_points - clubPoints.level_info.min_points))) *
                            100
                        )
                      )}%`
                    }}
                  />
                </div>
                <div className={`text-center mt-1 text-xs font-medium ${clubCardColors.light ? 'text-gray-700' : 'text-white/90'}`}>
                  {clubPoints.next_level.points_needed.toLocaleString()} pts para el siguiente nivel
                </div>
              </div>
            ) : (
              <div className={`text-center text-sm ${clubCardColors.light ? 'text-gray-700' : 'text-white/90'}`}>
                Nivel máximo alcanzado
              </div>
            )}
          </div>

          {/* Saldo de Cuenta - Igual al administrador */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col justify-center">
              <div className="text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl text-gray-600">💰</span>
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-3">Saldo de Cuenta</h3>
                
                <div className="space-y-2 mb-3">
                  {balanceARS !== 0 && (
                    <div className={`text-lg font-semibold ${
                      balanceARS > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(Math.abs(balanceARS), 'ARS')}
                    </div>
                  )}
                  {balanceUSD !== 0 && (
                    <div className={`text-lg font-semibold ${
                      balanceUSD > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(Math.abs(balanceUSD), 'USD')}
                    </div>
                  )}
                  {balanceARS === 0 && balanceUSD === 0 && (
                    <div className="text-lg font-semibold text-gray-600">
                      $0
                    </div>
                  )}
                </div>
                
                <div className="text-xs text-gray-500 mb-3">
                  {(balanceARS > 0 || balanceUSD > 0) ? 'Saldo pendiente' : 
                   (balanceARS < 0 || balanceUSD < 0) ? 'Saldo a favor' : 'Sin saldo'}
                </div>
                
                <div className="text-xs text-gray-400">
                  Mismo saldo que en administración
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-blue-600">
            {items.filter(d => d.tipo && ['X', 'A', 'B', 'C'].includes(d.tipo.toUpperCase())).length}
          </div>
          <div className="text-sm text-gray-600">Facturas</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-green-600">
            {items.filter(d => d.tipo === 'Recibo' || d.tipo === 'Recibo de Pago').length}
          </div>
          <div className="text-sm text-gray-600">Recibos</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-emerald-600">
            {items.filter(d => d.tipo && (d.tipo.includes('Nota') || ['NC', 'ND'].includes(d.tipo.toUpperCase()))).length}
          </div>
          <div className="text-sm text-gray-600">Notas</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
          <div className="text-2xl font-bold text-purple-600">
            {items.length}
          </div>
          <div className="text-sm text-gray-600">Total</div>
        </div>
      </div>

      {/* Controles de Búsqueda y Ordenamiento */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Búsqueda */}
          <div className="relative w-full lg:w-1/2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar documentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Ordenamiento */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Ordenar por:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'fecha' | 'monto' | 'tipo')}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="fecha">Fecha</option>
              <option value="monto">Monto</option>
              <option value="tipo">Tipo</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors"
            >
              {sortOrder === 'asc' ? '↑ Ascendente' : '↓ Descendente'}
            </button>
          </div>
        </div>
      </div>

      {filteredAndSortedItems.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay documentos</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm 
              ? `No se encontraron documentos que coincidan con "${searchTerm}"`
              : 'Aún no tienes documentos asociados a tu cuenta.'
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Número</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedItems.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getTipoColor(doc.tipo)}`}>
                        {getTipoDisplay(doc.tipo)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-medium">
                      {doc.numero || 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 max-w-xs truncate">
                      {doc.descripcion || 'Sin descripción'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(doc.fecha)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      {formatCurrency(doc.monto)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => generateAndDownloadPDF(doc)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors"
                          title="Descargar PDF"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </button>
                        {doc.url_archivo && (
                          <button
                            onClick={() => downloadFile(doc.url_archivo!, `documento_${doc.numero || doc.id}.pdf`, doc)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 transition-colors"
                            title="Descargar archivo original"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Archivo
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Resumen de resultados */}
      {filteredAndSortedItems.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-500">
          Mostrando {filteredAndSortedItems.length} de {items.length} documentos
          {searchTerm && ` que coinciden con "${searchTerm}"`}
        </div>
      )}
    </div>
  );
};

export default ClienteDocumentos;
